<template>
  <layout>
    <template v-slot:header>
      <global-navigation></global-navigation>
    </template>

    <main v-if="hasPage" class="impact-detail">
      <flex-header>
        <template v-slot:flex>
          <button
            v-if="mainImage.wares && page.embed && !videoActive"
            :class="['media-wrap', 'playhead', { contain: mainImage.uncropped }]"
            @click="videoActive = true"
            title="Watch Trailer"
          >
            <img :src="mainImage.wares" />
          </button>
          <div v-else-if="mainImage.wares && page.embed && videoActive" class="media-wrap">
            <iframe
              :src="embedSrc"
              width="640"
              height="360"
              frameborder="0"
              webkitallowfullscreen
              mozallowfullscreen
              allowfullscreen
            ></iframe>
          </div>
          <div v-else-if="mainImage.wares" :class="['media-wrap', { contain: mainImage.uncropped }]">
            <img :src="mainImage.wares" />
          </div>
        </template>
        <title-row
          :left="page.subtitle"
          :center="page.title"
          :right="page.year"
          :title-is-h1="true"
          :mobile-column="true"
        />
      </flex-header>

      <div class="intro-section">
        <div class="inner">
          <div class="content">
            <div v-if="page.introtext" class="intro-text" v-html="page.introtext"></div>

            <div v-if="introSections.length > 0" class="intro-items">
              <div v-for="(item, index) in introSections" class="intro-item" :key="index">
                <h2>{{ item.title }}</h2>
                <div v-html="item.text"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="page-content">
        <component v-for="(item, index) in page.pagecontent" :key="index" :is="item.component" :data="item"></component>

        <div v-if="page.collaborators" class="ending-section impact-module">
          <div class="inner">
            <h2>In Collaboration With</h2>
            <div v-html="page.collaborators" class="text"></div>
          </div>
        </div>

        <div v-if="page.press.length > 0" class="ending-section impact-module">
          <div class="inner">
            <h2>Press</h2>

            <ul>
              <li v-for="(item, index) in page.press" :key="index">
                <a :href="item.link" target="_blank">{{ item.title }}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </main>

    <template v-slot:footer>
      <global-footer :border="true"></global-footer>
    </template>
  </layout>
</template>

<script>
import PageMixin from '@/mixins/PageMixin'
import TitleRow from '@/components/TitleRow'
import FlexHeader from '@/components/FlexHeader'
import TextBlock from '@/components/TextBlock'
import CardsBlock from '@/components/CardsBlock'
import LogosectionBlock from '@/components/LogosectionBlock'
import TextimagesectionBlock from '@/components/TextimagesectionBlock'
import SliderBlock from '@/components/SliderBlock'
import VideoBlock from '@/components/VideoBlock'

export default {
  name: 'Page',
  mixins: [PageMixin],
  components: {
    TitleRow,
    FlexHeader,
    TextBlock,
    CardsBlock,
    LogosectionBlock,
    TextimagesectionBlock,
    SliderBlock,
    VideoBlock,
  },
  data() {
    return {
      videoActive: false,
    }
  },
  computed: {
    mainImage() {
      let image = this.page.featuredimage

      if (this.page.hero_image) {
        image = this.page.heroimage
      }

      return image
    },
    embedSrc() {
      if (this.page && this.page.embed) {
        if (this.page.embed.type === 'vimeo') {
          return `https://player.vimeo.com/video/${this.page.embed.id}?autoplay=1`
        }
        if (this.page.embed.type === 'youtube') {
          return `https://youtube.com/embed/${this.page.embed.id}?autoplay=1`
        }
      }
      return false
    },
    introSections() {
      return this.page.introsubsections.filter((item) => item.title && item.text)
    },
  },
}
</script>

<style scoped>
/**
 * Container
 */

main {
  margin-bottom: 4rem;
}

main > * + * {
  margin-top: 4rem;
}

main > *:nth-child(2) {
  margin-top: 0;
}

.ending-section {
  padding: 0 2rem;
}

.ending-section h2 {
  font: var(--nord);
}

.ending-section li {
  font: var(--serif-small);
}

.ending-section .inner {
  max-width: 1200px;
  margin: 0 auto;
  border-top: 1px dashed white;
  padding-top: 6rem;
}

.ending-section .text {
  font: var(--serif-small);
}

.ending-section .text a {
  text-decoration: underline;
}

/**
 * Grid
 */

.content {
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  padding-left: 2rem;
  padding-right: 2rem;
  grid-gap: 4rem 2rem;
}

.content-left {
  grid-column: 2 / 8;
}

.content-right {
  grid-column: 10 / 24;
}

.content.centered .content-right {
  grid-column: 6 / 20;
}

.content hr {
  margin: 0;
  grid-column: 1 / -1;
}

.content hr:first-child {
  margin-top: 0.5rem;
}

/**
 * Media
 */

.media-wrap {
  width: 100%;
  height: 100%;
  position: relative;
}

button.media-wrap {
  -webkit-appearance: none;
  -moz-appearance: none;
  border: none;
  background: none;
  cursor: pointer;
}

button.media-wrap.focus-visible {
  border: 1px dashed #fff;
}

.media-wrap iframe {
  position: absolute;
  top: 10%;
  left: 10%;
  height: 80%;
  width: 80%;
}

.media-wrap img {
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0.8;
}

.media-wrap.contain img {
  object-fit: contain;
}

.media-wrap.playhead::after {
  content: 'Watch';
  font: var(--nord);
  text-transform: uppercase;
  width: 20rem;
  height: 14rem;
  background-image: url(~@/assets/playhead.svg);
  background-size: 8.5rem;
  background-repeat: no-repeat;
  background-position: top center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  text-align: center;
  color: #fff;
}

main .intro-section {
  padding: 0 2rem;
}

.intro-section .inner {
  border-top: 1px dashed white;
}

.intro-section .content {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0 auto;
  max-width: 1200px;
  padding: 6rem 0;
}

/* .intro-text,
.intro-items {
  width: 50%;
} */

.intro-text {
  font: var(--serif-small);
  /* padding-right: 7rem; */
}

.intro-text::v-deep > * + p {
  margin-top: 2rem;
}

.intro-text::v-deep a {
  text-decoration: underline;
}

.intro-items {
  display: flex;
  flex-wrap: wrap;
  /* padding-left: 7rem; */
}

.intro-item {
  /* width: 50%; */
  width: 100%;
  /* padding-right: 2rem; */
}

.intro-item + .intro-item {
  margin-top: 5rem;
}

.intro-item div {
  font: var(--serif-small);
  word-wrap: break-word;
}

main .page-content {
  margin: 0;
  padding-bottom: 6rem;
}

@media (max-width: 1000px) {
  /**
   * Grid
   */

  .content {
    display: flex;
    flex-direction: column;
  }

  .content > *:not(:last-child) {
    margin-bottom: 4rem !important;
  }

  .content-left {
    order: 1;
  }

  .content hr:last-child {
    order: 2;
  }

  /**
   * Media
   */

  .media-wrap.playhead::after {
    content: '';
    height: 6.5rem;
    background-size: contain;
  }

  .media-wrap iframe {
    width: 90%;
    left: 5%;
  }

  .impact-detail::v-deep .title-row {
    flex-direction: column !important;
    align-items: center !important;
  }

  .intro-text,
  .intro-items {
    width: 100%;
    padding: 0;
  }

  .intro-text {
    margin: 0 !important;
  }

  .intro-item {
    width: 100%;
    padding: 0;
  }
}
</style>
